/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

export interface ILogoProps {
    image: Msdyn365.IImageData;
    gridSettings?: Msdyn365.IGridSettings;
    link?: ILinkData;
    className?: string;
    telemetryContent?: ITelemetryContent;
    requestContext: Msdyn365.IRequestContext;
    typeName?: string;
}

export interface ILinkData {
    linkUrl: Msdyn365.ILinkData;
    ariaLabel?: string;
    openInNewTab?: boolean;
    linkText?: string;
}

/**
 *
 * Logo component.
 * @extends {React.PureComponent<ILogoConfig>}
 */
export class Logo extends React.PureComponent<ILogoProps> {
    private readonly attributes?: Msdyn365.IDictionary<string>;

    constructor(props: ILogoProps) {
        super(props);
        const payLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.Logo);
        this.attributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    }

    public render(): JSX.Element {
        return <div className={this.props.className}>{this._renderLogo(this.props)}</div>;
    }

    private _renderLogo(config: ILogoProps): JSX.Element {
        if (config.link && config.link.linkUrl.destinationUrl) {
            return (
                <a
                    href={this._preservePassthroughQueryParams(config.link.linkUrl.destinationUrl)}
                    aria-label={config.link.ariaLabel}
                    target={config.link.openInNewTab ? '_blank' : undefined}
                    rel='noopener noreferrer'
                    {...this.attributes}
                >
                    {this._renderImage(config)}
                </a>
            );
        }
        return this._renderImage(config);
    }

    /**
     * Preserves selected query parameters (catalogid, domain) on the logo/home link
     * when the current page URL already carries them. This prevents losing the
     * B2B catalog context (or domain hint) when a user clicks the home/logo link
     * that is authored to point to '/' or an absolute destination on the same host.
     *
     * Mirrors the PASSTHROUGH_QUERY_PARAMS behavior of @msdyn365-commerce/core's
     * appendQueryParams / link-hydrator for raw author-supplied links that bypass
     * the hydrator.
     */
    private _preservePassthroughQueryParams(rawHref: string): string {
        const query = this.props.requestContext?.query;
        const catalogId = query?.catalogid;
        const domain = query?.domain;
        if (!catalogId && !domain) {
            return rawHref;
        }

        // Only operate on root-relative paths ('/...') and absolute http(s) URLs.
        // Leave everything else untouched: protocol-relative ('//host/path'),
        // hash-only ('#x'), query-only ('?x'), bare relative ('page'), and
        // non-http(s) schemes (mailto:, tel:, javascript:, etc.). Rewriting those
        // would strip the scheme or inject the current page path, corrupting the link.
        const isAbsoluteHttp = /^https?:\/\//i.test(rawHref);
        const isRootRelative = rawHref.startsWith('/') && !rawHref.startsWith('//');
        if (!isAbsoluteHttp && !isRootRelative) {
            return rawHref;
        }

        // Resolve the current page URL as the base. In this repo
        // requestContext.url.requestUrl is typed as URL but at runtime can be
        // either a URL instance (exposes .href) or a plain string, mirroring the
        // shared getCurrentUrl helpers (e.g. search-result-container url-utils).
        // Using requestUrl?.href alone would yield undefined for the string case
        // and silently skip preservation, so handle both shapes.
        const requestUrl = this.props.requestContext?.url?.requestUrl;
        const base = typeof requestUrl === 'string' ? requestUrl : requestUrl?.href;
        if (!base) {
            return rawHref;
        }
        try {
            const target = new URL(rawHref, base);
            // Defense in depth: only http(s) destinations may receive the params.
            if (target.protocol !== 'http:' && target.protocol !== 'https:') {
                return rawHref;
            }
            // Compare full origin (scheme + host + port), not just hostname, so
            // params are never leaked across a different port or protocol on the
            // same host (e.g. http: vs https:, or :444).
            const current = new URL(base);
            if (target.origin !== current.origin) {
                return rawHref;
            }
            if (catalogId && !target.searchParams.has('catalogid')) {
                target.searchParams.set('catalogid', catalogId);
            }
            // Respect the remove_domain_qsp feature flag, which the platform uses
            // to actively strip the domain query param (see removeDomainQspFromUrl
            // in retail-actions). Re-adding it unconditionally would fight that flag.
            if (domain && !this.props.requestContext?.features?.remove_domain_qsp && !target.searchParams.has('domain')) {
                target.searchParams.set('domain', domain);
            }
            return isAbsoluteHttp ? target.href : `${target.pathname}${target.search}${target.hash}`;
        } catch {
            return rawHref;
        }
    }

    private _renderImage(config: ILogoProps): JSX.Element {
        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=132&h=28&m=6', w: 0, h: 0 },
                lg: { q: 'w=160&h=48&m=6', w: 0, h: 0 }
            },
            lazyload: true
        };

        return (
            <Msdyn365.Image
                {...config.image}
                requestContext={this.props.requestContext}
                gridSettings={this.props.gridSettings!}
                imageSettings={(config && config.image && config.image.imageSettings) || defaultImageSettings}
                loadFailureBehavior='default'
                editProps={{
                    key: config.image || {},
                    requestContext: this.props.requestContext,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image'
                }}
                shouldSkipToMainImage
            />
        );
    }
}

export default Logo;
